import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";
import {
  BigCommerceProductFilter,
  ProductStatus,
} from "../../../../models/app/AppModels";
import {
  useGetBigCommerceProductsForLinkInventoryMutation,
  useMatchShopifyProductsWithDMSMutation,
} from "../../../../redux/reducer/features/Integration/DmsSetupSlice";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { LinkInventoryDto } from "../../../../models/features/Integration/shopify";
import { ProductType } from "../../../../models/features/Integration/DmsSetup";

interface BigCommerceVariantDto {
  id: number;
  productId: number;
  sku: string | null;
  skuId: number | null;
  price: number | null;
  calculatedPrice: number | null;
  salePrice: number | null;
  retailPrice: number | null;
  mpn: string | null;
  optionValues: BigCommerceOptionValue[] | null;
  isSynced: boolean;
}

interface BigCommerceOptionValue {
  id: number;
  label: string;
  optionDisplayName: string;
}

interface BigCommerceProductDto {
  id: number;
  name: string;
  type: string;
  sku: string | null;
  price: number;
  costPrice: number | null;
  retailPrice: number | null;
  salePrice: number | null;
  mapPrice: number | null;
  brandId: number | null;
  brandName: string | null;
  inventoryTracking: string;
  mpn: string | null;
  variants: BigCommerceVariantDto[] | null;
  isPriceHidden: boolean;
  customUrl?: { url: string; isCustomized: boolean };
}

interface ProductGridProps {
  handleNext: any;
  setSelectedProducts: (products: any[]) => void;
}

const BigCommerceProductGrid: React.FunctionComponent<ProductGridProps> = (
  props,
) => {
  const [products, setProducts] = React.useState<BigCommerceProductDto[]>([]);
  const [productsCount, setProductsCount] = React.useState<number>(0);
  const [searchProduct, setSearchProduct] = React.useState("");
  const [selectedProductIds, setSelectedProductIds] = React.useState<
    Set<number>
  >(new Set());
  const [productStatus, setProductStatus] = React.useState("all");
  const [productsMutation, productsMutationResult] =
    useGetBigCommerceProductsForLinkInventoryMutation();
  const [matchProductsMutation, matchProductsMutationResult] =
    useMatchShopifyProductsWithDMSMutation();

  const [paging, setPaging] = React.useState<BigCommerceProductFilter>({
    pageIndex: 0,
    pageSize: 50,
    totalRecords: 0,
    quickFilterValues: [],
    items: [],
  });

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPaging({
      ...paging,
      pageIndex: newPage,
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setPaging({
      ...paging,
      pageSize: parseInt(event.target.value, 10),
      pageIndex: 0,
    });
  };

  React.useEffect(() => {
    fetchProducts(paging);
  }, [paging, productStatus]);

  const fetchProducts = (pagingData: any) => {
    productsMutation({
      paging: {
        ...pagingData,
        productStatus:
          productStatus === "all"
            ? undefined
            : productStatus === "active"
              ? ProductStatus.Active
              : ProductStatus.Draft,
      },
    }).then((res: any) => {
      if (res && res.data && res.data.status === 200) {
        setProducts(res.data.entityList);
        setProductsCount(res.data.totalRecords || 0);
      }
    });
  };

  const debounce = (func: Function, delay: number) => {
    let timer: ReturnType<typeof setTimeout>;
    return function (this: any, ...args: any[]) {
      clearTimeout(timer);
      timer = setTimeout(() => func.apply(this, args), delay);
    };
  };

  const handleSearchTextChange = React.useCallback(
    debounce((value: any) => {
      setPaging({
        ...paging,
        quickFilterValues: value ? [value?.trim()] : [],
        pageIndex: 0,
      });
    }, 1000),
    [],
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchProduct(value);
    handleSearchTextChange(value);
  };

  const isAllVariantsSynced = (product: BigCommerceProductDto): boolean => {
    if (!product.variants || product.variants.length === 0) return false;
    return product.variants.every((v) => v.isSynced);
  };

  const mapSelectedProductsToLinkInventoryDto = (): LinkInventoryDto[] => {
    const linkDtos: LinkInventoryDto[] = [];
    products
      .filter((p) => selectedProductIds.has(p.id))
      .forEach((product) => {
        if (product.variants && product.variants.length > 0) {
          product.variants
            .filter((v) => !v.isSynced)
            .forEach((variant) => {
              linkDtos.push({
                variantId: variant.id,
                title:
                  variant.optionValues?.map((ov) => ov.label).join(" / ") ?? "",
                displayName: product.name,
                sku: variant.sku ?? "",
                price: variant.calculatedPrice,
                isSynced: variant.isSynced,
                productAggId: null,
                externalProductId: product.id,
                productTitle: product.name,
                vendor: product.brandName ?? "",
                status: "",
                dmsWholegood: null,
                dmsPart: null,
                isMatched: false,
                productType: ProductType.Wholegoods,
                inventoryItemId: 0,
                mpn: variant.mpn ?? "",
                handle: product.customUrl?.url ?? "",
              });
            });
        }
      });
    return linkDtos;
  };

  const handleNextStep = () => {
    const mappedProducts = mapSelectedProductsToLinkInventoryDto();
    matchProductsMutation({ request: mappedProducts }).then((res: any) => {
      if (res && res.data) {
        if (res.data.status === 200) {
          props.setSelectedProducts(res.data.entityList);
          props.handleNext();
        }
      }
    });
  };

  const handleSelectProduct = (productId: number) => {
    setSelectedProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    const selectableProducts = products.filter((p) => !isAllVariantsSynced(p));
    const selectableIds = selectableProducts.map((p) => p.id);
    const currentPageSelectedCount = selectableIds.filter((id) =>
      selectedProductIds.has(id),
    ).length;

    if (
      currentPageSelectedCount === selectableProducts.length &&
      selectableProducts.length > 0
    ) {
      setSelectedProductIds((prev) => {
        const next = new Set(prev);
        selectableIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedProductIds((prev) => {
        const next = new Set(prev);
        selectableIds.forEach((id) => next.add(id));
        return next;
      });
    }
  };

  const handleSelectChange = (event: any) => {
    setProductStatus(event.target.value);
  };

  const selectableProducts = products.filter((p) => !isAllVariantsSynced(p));
  const selectableIds = selectableProducts.map((p) => p.id);
  const currentPageSelectedCount = selectableIds.filter((id) =>
    selectedProductIds.has(id),
  ).length;
  const isCurrentPageAllSelected =
    currentPageSelectedCount === selectableProducts.length &&
    selectableProducts.length > 0;
  const isCurrentPageIndeterminate =
    currentPageSelectedCount > 0 &&
    currentPageSelectedCount < selectableProducts.length;

  function Row({ row }: { row: BigCommerceProductDto }) {
    const [open, setOpen] = React.useState(false);
    const allSynced = isAllVariantsSynced(row);
    const isSelected = selectedProductIds.has(row.id);

    return (
      <React.Fragment>
        <TableRow sx={{ "& > *": { borderBottom: open ? "none" : undefined } }}>
          <TableCell sx={{ padding: "7px", width: "1%" }}>
            <Checkbox
              checked={isSelected}
              disabled={allSynced}
              onChange={() => handleSelectProduct(row.id)}
              onClick={(e) => e.stopPropagation()}
            />
          </TableCell>
          <TableCell sx={{ padding: "7px", width: "1%" }}>
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
          <TableCell sx={{ padding: "10px" }}>{row.brandName ?? "-"}</TableCell>
          <TableCell sx={{ padding: "10px" }}>{row.name}</TableCell>
          <TableCell sx={{ padding: "10px" }}>{row.mpn ?? "-"}</TableCell>
          <TableCell align="center" sx={{ padding: "10px" }}>
            {row.variants && row.variants.length > 0 ? (
              <Chip
                label={`${row.variants.filter((v) => v.isSynced).length}/${row.variants.length}`}
                size="small"
                color={
                  row.variants.every((v) => v.isSynced) ? "success" : "error"
                }
              />
            ) : (
              "-"
            )}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell sx={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ marginLeft: 6, marginY: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>SKU</TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Price ($)
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {row.variants && row.variants.length > 0 ? (
                      row.variants.map((variant) => (
                        <TableRow key={variant.id}>
                          <TableCell>{variant.sku ?? "-"}</TableCell>
                          <TableCell align="center">
                            {variant?.calculatedPrice?.toFixed(2) ?? "0.00"}
                          </TableCell>
                          <TableCell align="center">
                            {variant.isSynced ? (
                              <Chip
                                label="Synced"
                                color="success"
                                size="small"
                              />
                            ) : (
                              <Chip
                                label="Not Synced"
                                color="error"
                                size="small"
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          No variants
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }

  return (
    <>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        gap={2}
        p={0.5}
      >
        <Alert severity="info">
          Select BigCommerce products to link with DMS products. TargetWeb will
          automatically try to find the best matches.
        </Alert>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            alignItems: "center",
          }}
        >
          <Button
            disabled={
              selectedProductIds.size === 0 ||
              matchProductsMutationResult.isLoading
            }
            size="small"
            variant="contained"
            onClick={handleNextStep}
            endIcon={
              matchProductsMutationResult.isLoading ? (
                <CircularProgress color="inherit" size={16} />
              ) : null
            }
          >
            Next
          </Button>
        </Box>
      </Box>
      <Card sx={{ borderColor: "rgba(0, 0, 0, 0.12)", border: "1px outset" }}>
        <Box
          display={"flex"}
          justifyContent={"flex-end"}
          gap={2}
          alignItems={"center"}
        >
          <Select
            value={productStatus}
            sx={{ width: "130px", mt: 0.5 }}
            onChange={handleSelectChange}
            variant="outlined"
            size="small"
          >
            <MenuItem selected value="all">
              All
            </MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
          </Select>
          <TextField
            id="search"
            name="search"
            placeholder="Search.."
            variant="standard"
            size="small"
            onChange={handleChange}
            value={searchProduct}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" sx={{ fontSize: "20px" }} />
                </InputAdornment>
              ),
              endAdornment: searchProduct && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() =>
                      handleChange({
                        target: { value: "" },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                    edge="end"
                  >
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <TableContainer component={Paper} sx={{ height: "59vh" }}>
          <Table
            aria-label="collapsible table"
            sx={{ borderCollapse: "separate" }}
          >
            <TableHead
              sx={{
                position: "sticky",
                zIndex: 100,
                top: 0,
                background: "white",
              }}
            >
              <TableRow sx={{ height: "2vh" }}>
                <TableCell sx={{ p: "7px", width: "1%" }}>
                  <Checkbox
                    checked={isCurrentPageAllSelected}
                    onChange={handleSelectAll}
                    indeterminate={isCurrentPageIndeterminate}
                  />
                </TableCell>
                <TableCell sx={{ padding: 0, width: "1%" }} />
                <TableCell sx={{ padding: 0, width: "10%" }}>
                  <span style={{ color: "#E0E0E0" }}>|</span> Manufacturer
                </TableCell>
                <TableCell sx={{ padding: 0 }}>
                  <span style={{ color: "#E0E0E0" }}>|</span> Product Title
                </TableCell>
                <TableCell sx={{ padding: 0 }}>
                  <span style={{ color: "#E0E0E0" }}>|</span> MPN
                </TableCell>
                <TableCell align="center" sx={{ padding: 0, width: "7%" }}>
                  <span style={{ color: "#E0E0E0" }}>|</span> Synced Variants
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {productsMutationResult.isLoading ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box
                      justifyContent="center"
                      alignItems="center"
                      display="flex"
                      sx={{ height: "60vh" }}
                    >
                      <CircularProgress size="2.2rem" color="inherit" />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : products && products.length > 0 ? (
                products.map((row: BigCommerceProductDto) => (
                  <Row key={row.id} row={row} />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box
                      justifyContent="center"
                      alignItems="center"
                      display="flex"
                      sx={{ height: "60vh" }}
                    >
                      <Typography>No Rows to Show</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingLeft: 2,
            paddingRight: 1,
            borderTop: "1px solid rgba(224, 224, 224, 1)",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", flex: "0 0 auto" }}>
            {selectedProductIds.size > 0 && (
              <Typography variant="body2" sx={{ color: "rgba(0, 0, 0, 0.87)" }}>
                {selectedProductIds.size}{" "}
                {selectedProductIds.size === 1 ? "row" : "rows"} selected
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", flex: "0 0 auto" }}>
            <TablePagination
              rowsPerPageOptions={[50, 100, 250]}
              count={productsCount}
              rowsPerPage={paging.pageSize}
              page={paging.pageIndex}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelDisplayedRows={({ from, to, count }) => {
                return `${from}–${to} of ${count}`;
              }}
              sx={{
                border: "none",
                "& .MuiTablePagination-toolbar": {
                  paddingLeft: 0,
                  minHeight: "auto",
                },
              }}
            />
          </Box>
        </Box>
      </Card>
    </>
  );
};

export default BigCommerceProductGrid;
